/**
 * Employee Asset Assignments API client.
 *
 * Backend: app/api/routers/employee_assets.py — mounted at /hr/employee-assets.
 *
 * Tracks issued PPE / uniforms / tools / ID cards / laptops / mobiles / vehicles.
 * On separation, outstanding (not yet RETURNED) rows feed asset_damage_recovery
 * in F&F.
 */
import apiClient from '@/shared/api/client'

export const ASSET_TYPES = [
  'PPE', 'UNIFORM', 'TOOL', 'ID_CARD', 'LAPTOP', 'MOBILE', 'VEHICLE', 'OTHER',
] as const
export type AssetType = (typeof ASSET_TYPES)[number]

export const ASSET_RETURN_STATUSES = [
  'ISSUED', 'RETURNED', 'LOST', 'DAMAGED', 'PARTIALLY_DAMAGED',
] as const
export type AssetReturnStatus = (typeof ASSET_RETURN_STATUSES)[number]

export interface EmployeeAssetAssignment {
  id: string
  employee_id: string
  employee_code: string | null
  emp_name: string | null
  asset_type: AssetType
  asset_description: string
  asset_code: string | null
  issued_on: string
  issued_by: string | null
  issue_value: number | null
  return_status: AssetReturnStatus
  returned_on: string | null
  damage_recovery_amount: number | null
  remarks: string | null
  created_at: string | null
}

const BASE = '/hr/employee-assets'

export const employeeAssetsApi = {
  list: async (params: {
    employee_id?: string
    asset_type?: AssetType
    return_status?: AssetReturnStatus
    outstanding_only?: boolean
  } = {}) => (await apiClient.get<EmployeeAssetAssignment[]>(BASE, { params })).data,

  issue: async (body: {
    employee_id: string
    asset_type: AssetType
    asset_description: string
    asset_code?: string
    issued_on: string
    issue_value?: number
    remarks?: string
  }) => (await apiClient.post<EmployeeAssetAssignment>(`${BASE}/issue`, body)).data,

  recordReturn: async (id: string, body: {
    return_status: AssetReturnStatus
    returned_on?: string
    damage_recovery_amount?: number
    remarks?: string
  }) => (await apiClient.post<EmployeeAssetAssignment>(
    `${BASE}/${id}/record-return`, body,
  )).data,

  outstanding: async (employee_id: string) =>
    (await apiClient.get<EmployeeAssetAssignment[]>(
      `${BASE}/outstanding/${employee_id}`,
    )).data,
}

export const RETURN_STATUS_COLOR: Record<AssetReturnStatus, string> = {
  ISSUED: 'blue',
  RETURNED: 'green',
  LOST: 'volcano',
  DAMAGED: 'red',
  PARTIALLY_DAMAGED: 'orange',
}

export const ASSET_TYPE_LABEL: Record<AssetType, string> = {
  PPE: 'PPE',
  UNIFORM: 'Uniform',
  TOOL: 'Tool',
  ID_CARD: 'ID Card',
  LAPTOP: 'Laptop',
  MOBILE: 'Mobile',
  VEHICLE: 'Vehicle',
  OTHER: 'Other',
}
